import { FC, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";

import { formatDateForMapReport } from "../../../utils/formatDate";

import {
  RulerControl,
  ZoomControl,
  Placemark,
  Clusterer,
  YMaps,
  Map,
} from "@pbe/react-yandex-maps";

import Loader from "../../custom/loader/loader";
import CustomMultiSelect from "../../custom/customMultiSelect/customMultiSelect";
import Select from "../../custom/select/select";

import { actionGetUserReportsFilter } from "../../../redux/action/userReports/actionUserReports";

import { ReactComponent as ArrowIcon } from "../../../assets/icons/chevron.svg";

import {
  actionGetMap,
  actionGetUserReports,
} from "../../../redux/action/map/actionMap";
import {
  reducerMapsInputValues,
  reducerGetReport,
} from "../../../redux/reducer/map/reducer/reducerMapsList";
import { AppDispatch } from "../../../redux/reducer/store";

import { IUserOptions } from "../../../types/interfaces";

import styles from "./map.module.scss";
import { IReportsOptions } from "../../../types/reportsInterfaces";

const ratingOptions = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

const typeOptions = [
  { value: "Active", label: "Обслуживаются" },
  { value: "Paused", label: "Обслуживаются (временно неактивна)" },
  { value: "Vacancy", label: "Обслуживаются (вакансия)" },
  { value: "Available", label: "Доступны к обслуживанию (действующие сети)" },
  {
    value: "AvailableNewChains",
    label: "Доступны к обслуживанию (новые сети)",
  },
];

const typeOptionsColor = [
  { value: "Active", color: "rgb(160, 212, 203)" },
  { value: "Paused", color: "rgb(160, 212, 203)" },
  { value: "Vacancy", color: "rgb(255, 249, 155)" },
  { value: "Available", color: "rgb(237, 156, 166)" },
  { value: "AvailableNewChains", color: "rgb(201, 185, 247)" },
];
const typeTextOptions = {
  Active: "ТТ обслуживается в настоящий момент по контракту.",
  Paused: "ТТ обслуживается в настоящий момент по контракту.",
  Vacancy: "ТТ принята к обслуживанию, но в настоящий момент ТТ вакантна.",
  Available:
    "ТТ обслуживается в настоящий момент по другим контрактам других заказчиков. Готовы оперативно подключить ТТ.",
  AvailableNewChains:
    "ТТ обслуживается в настоящий момент по другим контрактам других заказчиков.",
  NotActive:
    "ТТ не обслуживается в настоящий момент по другим контрактам других заказчиков.",
};

const dayOptions = [
  { value: "MONDAY", label: "Понедельник" },
  { value: "TUESDAY", label: "Вторник" },
  { value: "WEDNESDAY", label: "Среда" },
  { value: "THURSDAY", label: "Четверг" },
  { value: "FRIDAY", label: "Пятница" },
  { value: "SATURDAY", label: "Суббота" },
  { value: "SUNDAY", label: "Воскресенье" },
];

const periodOptions = [
  { value: "month", label: "Месяц" },
  { value: "threemonth", label: "3 месяца" },
  { value: "halfYear", label: "Пол года" },
];

const YMAP_API_KEY = process.env.REACT_APP_YMAP_KEY;

const CustomerMap: FC = () => {
  const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;
  const shiftAmount = 157;

  const dispatch = useDispatch<AppDispatch>();

  const filtersRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const { regionsOptions, contractsOptions, chainsOptions } = useSelector(
    (state: any) => state.userReports.userReports
  );
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const { inputValues, data, counter, mapCenter, zoom } = useSelector(
    (state: any) => state.map.mapsList
  );
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const existingCoordinates = new Set<string>();

  const isShow =
    inputValues &&
    inputValues.mapModes &&
    (inputValues.mapModes.includes("Active") ||
      inputValues.mapModes.includes("Paused") ||
      inputValues.mapModes.includes("Vacancy"));

  const [isPending, setIsPending] = useState<boolean>(false);
  const [ymaps, setYmaps] = useState<any | null>(null);
  const [isReport, setIsReport] = useState<
    { key: number; value: IReportsOptions }[] | null
  >([]);
  const [currentShift, setCurrentShift] = useState<number>(0);
  const [isVisibleDay, setIsVisibleDay] = useState<boolean>(true);
  const [isWrapperWidth, setIsWrapperWidth] = useState<number>(0);
  const [isVisibleRating, setIsVisibleRating] = useState<boolean>(false);
  const [isVisionLeftButton, setIsVisionLeftButton] = useState<boolean>(false);
  const [isVisionRightButton, setIsVisionRightButton] = useState<boolean>(true);
  const [isVisibleQualityService, setIsVisibleQualityService] =
    useState<boolean>(inputValues?.qualityService);
  const [isFilters, setIsFilters] = useState<string[]>([
    "mapModes",
    "regions",
    "chains",
    "contract",
    "days",
    "combineDays",
    "demand",
    "qualityService",
  ]);

  const layout = (value: {
    rating: number;
    status: string;
    days?: string[];
    quality?: number | null;
  }) => {
    const weekList = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];
    const indices = (mainArray: string[]) => {
      return mainArray.map((item) => weekList.indexOf(item));
    };
    const count = value.quality ? `${value.quality}%` : "";
    const template = `<div class="custom-icon">
     ${
       isVisibleQualityService &&
       (value.status === "Active" ||
         value.status === "Vacancy" ||
         value.status === "Paused")
         ? `<p class="custom-icon__text">${count}</p>`
         : ""
     }
     <div class="custom-icon__container">
        <div class="icon-text">
          ${
            value?.days && value?.days?.length
              ? isVisibleDay
                ? value.days.length
                : value.days[0]
              : ""
          }
        </div>
       <div class="iconMark icon-status_${value.status}"></div>
       ${
         value.days && value.days.length > 0
           ? indices(value.days)
               .map(
                 (el: number, index: number) =>
                   `<div class="iconDay iconDay_${el} icon-status_${value.status}"></div>`
               )
               .join("")
           : ""
       }
     </div>
     ${`<div class="iconRating icon-status_${value.status}" style="width: ${
       value.rating && isVisibleRating ? value.rating * 10 : ""
     }px"></div>`}
   </div>`;

    const layoutInstance =
      ymaps &&
      ymaps.templateLayoutFactory.createClass(template, {
        build: function () {
          layoutInstance.superclass.build.call(this);
          this.getData().options.set("shape", {
            type: "Circle",
            coordinates: [18, 36],
            radius: 26 / 2,
          });
        },
      });

    return layoutInstance;
  };

  const handleChangeSelect = (
    event: { value: string; label: string },
    name: string
  ) => {
    dispatch(
      reducerMapsInputValues({
        ...inputValues,
        [name]: { id: event.value, name: event.label },
      })
    );
    setIsPending(true);
    dispatch(actionGetMap()).then(() => {
      setIsPending(false);
    });
    handleChangeFilters();
  };

  const handleChangeCheckbox = (value: boolean, name: string) => {
    if (name === "demand") {
      setIsVisibleRating(!isVisibleRating);
      setIsFilters((prevFilters: string[]) => {
        const updatedFilters = [...prevFilters];

        if (!inputValues.demand) {
          updatedFilters.push("demand");
        } else {
          const index = updatedFilters.indexOf("demand");
          if (index !== -1) {
            updatedFilters.splice(index, 1);
          }
        }

        return updatedFilters;
      });
    }
    if (name === "qualityService") {
      setIsVisibleQualityService(!isVisibleQualityService);
      setIsFilters((prevFilters: string[]) => {
        const updatedFilters = [...prevFilters];

        if (!inputValues.qualityService) {
          updatedFilters.push("qualityService");
        } else {
          const index = updatedFilters.indexOf("qualityService");
          if (index !== -1) {
            updatedFilters.splice(index, 1);
          }
        }

        return updatedFilters;
      });
    }
    dispatch(
      reducerMapsInputValues({
        ...inputValues,
        [name]: value,
      })
    );
  };

  const handleChangeFilters = () => {
   dispatch(reducerGetReport(null));
  };

  const getUniqueCoordinates = (coordinates: number[]) => {
    let uniqueCoordinates = [...coordinates];
    let coordinateString = uniqueCoordinates.toString();

    while (existingCoordinates.has(coordinateString)) {
      uniqueCoordinates = [
        uniqueCoordinates[0] + 0.00002,
        uniqueCoordinates[1] + 0.00005,
      ];
      coordinateString = uniqueCoordinates.toString();
    }

    existingCoordinates.add(coordinateString);
    return uniqueCoordinates;
  };

  const handleClickOnLeft = () => {
    const filtersElement = filtersRef.current;
    if (filtersElement) {
      const newShift = currentShift + shiftAmount;
      const a = isShow ? -1 : 3;
      const containerWidth = shiftAmount * (isFilters.length - a);
      const remainingShift = containerWidth + currentShift;
      setIsVisionRightButton(isWrapperWidth - shiftAmount < remainingShift);

      if (newShift < shiftAmount) {
        filtersElement.style.left = `${newShift}px`;
        setCurrentShift(newShift);
        setIsVisionLeftButton(newShift !== 0);
      }
    }
  };

  const handleClickOnRight = () => {
    setIsVisionLeftButton(true);
    const filtersElement = filtersRef.current;

    if (filtersElement) {
      const reductionFactor = isShow ? 0 : 4;
      const containerWidth = shiftAmount * (isFilters.length - reductionFactor);
      const remainingShift = containerWidth + currentShift;

      if (isWrapperWidth - shiftAmount < remainingShift) {
        const newShift = currentShift - shiftAmount;
        filtersElement.style.left = `${newShift}px`;
        setCurrentShift(newShift);
        setIsVisionRightButton(
          isWrapperWidth - shiftAmount < remainingShift - shiftAmount
        );
      }
    }
  };

  useEffect(() => {
    const filtersElement = filtersRef.current;

    if (filtersElement) {
      const reductionFactor = isShow ? 0 : 4;
      const containerWidth = shiftAmount * (isFilters.length - reductionFactor);
      const contentWidth = filtersElement.scrollWidth;
      const remainingShift = containerWidth + currentShift;
      setIsVisionRightButton(isWrapperWidth - shiftAmount < remainingShift);
      if (currentShift > contentWidth - containerWidth) {
        setIsVisionRightButton(false);
      }
    }
    // eslint-disable-next-line
  }, [isFilters]);

  useEffect(() => {
    if (contractsOptions) {
      const sortedContracts = [...contractsOptions].sort((a, b) =>
        a.label.localeCompare(b.label)
      );

      dispatch(
        reducerMapsInputValues({
          ...inputValues,
          contract: [
            { id: sortedContracts[0].value, name: sortedContracts[0].label },
          ],
        })
      );
      setIsPending(true);
      dispatch(actionGetMap()).then(() => {
        setIsPending(false);
      });
    }
    // eslint-disable-next-line
  }, [contractsOptions]);

  useEffect(() => {
    setIsVisibleDay(inputValues.combineDays);
    // eslint-disable-next-line
  }, [inputValues]);

  useEffect(() => {
    dispatch(actionGetUserReportsFilter());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const filtersElement = filtersRef.current;

    if (filtersElement) {
      const containerWidth = filtersElement.clientWidth;
      const contentWidth = filtersElement.scrollWidth;
      setIsVisionRightButton(contentWidth >= containerWidth);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const updateButtonVisibility = () => {
      const filtersElement = filtersRef.current;

      if (filtersElement) {
        const containerWidth = filtersElement.clientWidth;
        const contentWidth = filtersElement.scrollWidth;
        setIsVisionRightButton(contentWidth >= containerWidth);
      }

      if (wrapperRef.current) {
        const wrapper = wrapperRef.current.getBoundingClientRect().width - 70;
        const count = Math.floor(wrapper / 157);
        console.log(wrapper);
        setIsWrapperWidth(count * 157);
      }
    };

    updateButtonVisibility();

    window.addEventListener("resize", updateButtonVisibility);

    return () => {
      window.removeEventListener("resize", updateButtonVisibility);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className={clsx(
        JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
          isOpenSidebar &&
          styles.wrapper__map_active,
        !isOpenSidebar && styles.wrapper__map_closed,
        styles.wrapper__map
      )}
    >
      <div className="loader__wrapper">{isPending && <Loader />}</div>
      <div className={styles.counters}>
        <div className={styles.counters__wrapper}>
          {counter &&
            counter.map(
              (
                el: {
                  count: number;
                  status: string;
                  text: string;
                },
                index: number
              ) => {
                const isNotActive = el.status === "NotActive";
                const shouldDisplay =
                  !isNotActive && inputValues.mapModes.includes(el.status);
                return (
                  shouldDisplay && (
                    <div className={styles.counters__item} key={index}>
                      <p dangerouslySetInnerHTML={{ __html: el.text }}></p>
                      <div
                        className={clsx(
                          styles.counters__circle,
                          styles[`counters__circle_${el.status}`]
                        )}
                      >
                        <p>{el.count}</p>
                      </div>
                    </div>
                  )
                );
              }
            )}
        </div>
      </div>
      <div className="filters__container__mobile">
        <div
          className={clsx(
            styles.map__filters__mobile,
            "multiselect__filter_block"
          )}
        >
          <CustomMultiSelect
            setLoading={setIsPending}
            inputValues={inputValues}
            options={typeOptions}
            isShowPlaceholder={true}
            pageName="mapTypes1"
            placeholder="Тип"
            name="mapModes"
            limit={0}
            reducer={reducerMapsInputValues}
            resetPages={handleChangeFilters}
            action={actionGetMap}
          />
        </div>
        <div
          className={clsx(
            styles.map__filters__mobile,
            "multiselect__filter_block"
          )}
        >
          <CustomMultiSelect
            setLoading={setIsPending}
            inputValues={inputValues}
            options={regionsOptions}
            isShowPlaceholder={true}
            pageName="mapTypes"
            placeholder="Регионы"
            name="regions"
            limit={0}
            reducer={reducerMapsInputValues}
            resetPages={handleChangeFilters}
            action={actionGetMap}
          />
        </div>
        <div
          className={clsx(
            styles.map__filters__mobile,
            "multiselect__filter_block"
          )}
        >
          <CustomMultiSelect
            setLoading={setIsPending}
            inputValues={inputValues}
            options={chainsOptions}
            isShowPlaceholder={true}
            pageName="mapTypes"
            placeholder="Сети"
            name="chains"
            limit={0}
            reducer={reducerMapsInputValues}
            resetPages={handleChangeFilters}
            action={actionGetMap}
          />
        </div>
        {contractsOptions && (
          <div
            className={clsx(
              styles.map__filters__mobile,
              "multiselect__filter_block"
            )}
          >
            <CustomMultiSelect
              setLoading={setIsPending}
              inputValues={inputValues}
              options={contractsOptions}
              isShowPlaceholder={true}
              pageName="mapContract"
              placeholder="Контракт"
              name="contract"
              limit={10}
              reducer={reducerMapsInputValues}
              resetPages={handleChangeFilters}
              action={actionGetMap}
              activeOptions={inputValues.contract}
            />
          </div>
        )}{" "}
        {isShow && (
          <div
            className={clsx(
              styles.map__filters__mobile,
              "multiselect__filter_block"
            )}
          >
            <CustomMultiSelect
              setLoading={setIsPending}
              inputValues={inputValues}
              options={dayOptions}
              isShowPlaceholder={true}
              pageName="mapTypes1"
              placeholder="Дни недели"
              name="days"
              limit={0}
              reducer={reducerMapsInputValues}
              resetPages={handleChangeFilters}
              action={actionGetMap}
            />
          </div>
        )}{" "}
        {isShow && (
          <div
            className={clsx(
              styles.map__filters__mobile,
              styles.criterias__list_elem
            )}
          >
            <label className={styles.checkbox__container}>
              {" "}
              <input
                name="combineDays"
                checked={inputValues.combineDays}
                type="checkbox"
                onChange={() => {
                  handleChangeCheckbox(!inputValues.combineDays, "combineDays");
                }}
              />{" "}
              <span className={styles.multi__reason_checkmark}></span>{" "}
            </label>{" "}
            Объединить дни недели
          </div>
        )}{" "}
        {!inputValues.mapModes.includes("NotActive") && (
          <div
            className={clsx(
              styles.map__filters__mobile,
              styles.criterias__list_elem,
              styles.map__filters__mobile__full
            )}
          >
            <label className={styles.checkbox__container}>
              {" "}
              <input
                name="demand"
                checked={inputValues.demand}
                type="checkbox"
                onChange={() => {
                  handleChangeCheckbox(!inputValues.demand, "demand");
                }}
              />{" "}
              <span className={styles.multi__reason_checkmark}></span>{" "}
            </label>{" "}
            Востребованность ТТ
          </div>
        )}{" "}
        {inputValues.demand && (
          <div
            className={clsx(
              styles.map__filters__mobile,
              "multiselect__filter_block"
            )}
          >
            <CustomMultiSelect
              setLoading={setIsPending}
              inputValues={inputValues}
              options={ratingOptions}
              isShowPlaceholder={true}
              pageName="mapTypes1"
              placeholder="Рейтинг"
              name="rating"
              limit={0}
              reducer={reducerMapsInputValues}
              resetPages={handleChangeFilters}
              action={actionGetMap}
            />
          </div>
        )}{" "}
        {isShow && (
          <div className={clsx(
              styles.map__filters__mobile,
              styles.criterias__list_elem,
              styles.map__filters__mobile__full,
            )}>
            <label className={styles.checkbox__container}>
              {" "}
              <input
                name="qualityService"
                checked={inputValues.qualityService}
                type="checkbox"
                onChange={() => {
                  handleChangeCheckbox(
                    !inputValues.qualityService,
                    "qualityService"
                  );
                }}
              />{" "}
              <span className={styles.multi__reason_checkmark}></span>{" "}
            </label>{" "}
            Качество обслуживания ТТ
          </div>
        )}{" "}
        {inputValues.qualityService && isShow && (
          <div
            className={clsx(styles.map__select, "filter__block", styles.map__filters__mobile, 
              styles.map__filters__mobile__full)}
          >
            <Select
              className={styles.filterSelect}
              value={
                inputValues?.period && {
                  value: inputValues.period.id,
                  label: inputValues.period.name,
                }
              }
              propsChange={(event: { value: string; label: string }) =>
                handleChangeSelect(event, "period")
              }
              options={periodOptions}
              placeholder={inputValues?.period ? inputValues?.period : "Период"}
              span="Период"
              name="period"
              disabled={false}
              isShowPlaceholder={inputValues?.period && inputValues?.period}
            />
          </div>
        )}
      </div>
      <div className={styles.map}>
        <div className={styles.map__block}>
          <div ref={wrapperRef} className={styles.container}>
            <ArrowIcon
              style={{ visibility: isVisionLeftButton ? "visible" : "hidden" }}
              className={clsx(styles.map__arrow, styles.map__arrow_left)}
              onClick={handleClickOnLeft}
            />
            <div
              style={{ width: `${isWrapperWidth}px` }}
              className={clsx(styles.map__wrapper)}
            >
              <div ref={filtersRef} className={clsx(styles.map__filters)}>
                <div
                  className="multiselect__filter_block"
                  style={{ width: "150px" }}
                >
                  <CustomMultiSelect
                    setLoading={setIsPending}
                    inputValues={inputValues}
                    options={typeOptions}
                    isShowPlaceholder={true}
                    pageName="mapTypes1"
                    placeholder="Тип"
                    name="mapModes"
                    limit={0}
                    reducer={reducerMapsInputValues}
                    resetPages={handleChangeFilters}
                    action={actionGetMap}
                  />
                </div>
                <div
                  className="multiselect__filter_block"
                  style={{ width: "150px" }}
                >
                  <CustomMultiSelect
                    setLoading={setIsPending}
                    inputValues={inputValues}
                    options={regionsOptions}
                    isShowPlaceholder={true}
                    pageName="mapTypes"
                    placeholder="Регионы"
                    name="regions"
                    limit={0}
                    reducer={reducerMapsInputValues}
                    resetPages={handleChangeFilters}
                    action={actionGetMap}
                  />
                </div>
                <div
                  className="multiselect__filter_block"
                  style={{ width: "150px" }}
                >
                  <CustomMultiSelect
                    setLoading={setIsPending}
                    inputValues={inputValues}
                    options={chainsOptions}
                    isShowPlaceholder={true}
                    pageName="mapTypes"
                    placeholder="Сети"
                    name="chains"
                    limit={0}
                    reducer={reducerMapsInputValues}
                    resetPages={handleChangeFilters}
                    action={actionGetMap}
                  />
                </div>
                {contractsOptions && (
                  <div
                    className="multiselect__filter_block"
                    style={{ width: "150px" }}
                  >
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      inputValues={inputValues}
                      options={contractsOptions}
                      isShowPlaceholder={true}
                      pageName="mapContract"
                      placeholder="Контракт"
                      name="contract"
                      limit={10}
                      reducer={reducerMapsInputValues}
                      resetPages={handleChangeFilters}
                      action={actionGetMap}
                      activeOptions={inputValues.contract}
                    />
                  </div>
                )}{" "}
                {isShow && (
                  <div
                    className="multiselect__filter_block"
                    style={{ width: "150px" }}
                  >
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      inputValues={inputValues}
                      options={dayOptions}
                      isShowPlaceholder={true}
                      pageName="mapTypes1"
                      placeholder="Дни недели"
                      name="days"
                      limit={0}
                      reducer={reducerMapsInputValues}
                      resetPages={handleChangeFilters}
                      action={actionGetMap}
                    />
                  </div>
                )}{" "}
                {isShow && (
                  <div
                    className={styles.criterias__list_elem}
                    style={{ width: "150px" }}
                  >
                    <label className={styles.checkbox__container}>
                      {" "}
                      <input
                        name="combineDays"
                        checked={inputValues.combineDays}
                        type="checkbox"
                        onChange={() => {
                          handleChangeCheckbox(
                            !inputValues.combineDays,
                            "combineDays"
                          );
                        }}
                      />{" "}
                      <span className={styles.multi__reason_checkmark}></span>{" "}
                    </label>{" "}
                    Объединить дни недели
                  </div>
                )}{" "}
                {!inputValues.mapModes.includes("NotActive") && (
                  <div className={styles.criterias__list_elem}>
                    <label className={styles.checkbox__container}>
                      {" "}
                      <input
                        name="demand"
                        checked={inputValues.demand}
                        type="checkbox"
                        onChange={() => {
                          handleChangeCheckbox(!inputValues.demand, "demand");
                        }}
                      />{" "}
                      <span className={styles.multi__reason_checkmark}></span>{" "}
                    </label>{" "}
                    Востребованность ТТ
                  </div>
                )}{" "}
                {inputValues.demand && (
                  <div
                    className="multiselect__filter_block"
                    style={{ width: "150px" }}
                  >
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      inputValues={inputValues}
                      options={ratingOptions}
                      isShowPlaceholder={true}
                      pageName="mapTypes1"
                      placeholder="Рейтинг"
                      name="rating"
                      limit={0}
                      reducer={reducerMapsInputValues}
                      resetPages={handleChangeFilters}
                      action={actionGetMap}
                    />
                  </div>
                )}{" "}
                {isShow && (
                  <div className={styles.criterias__list_elem}>
                    <label className={styles.checkbox__container}>
                      {" "}
                      <input
                        name="qualityService"
                        checked={inputValues.qualityService}
                        type="checkbox"
                        onChange={() => {
                          handleChangeCheckbox(
                            !inputValues.qualityService,
                            "qualityService"
                          );
                        }}
                      />{" "}
                      <span className={styles.multi__reason_checkmark}></span>{" "}
                    </label>{" "}
                    Качество обслуживания ТТ
                  </div>
                )}{" "}
                {inputValues.qualityService && isShow && (
                  <div
                    className={clsx(styles.map__select, "filter__block")}
                  >
                    <Select
                      className={styles.filterSelect}
                      value={
                        inputValues?.period && {
                          value: inputValues.period.id,
                          label: inputValues.period.name,
                        }
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        handleChangeSelect(event, "period")
                      }
                      options={periodOptions}
                      placeholder={
                        inputValues?.period ? inputValues?.period : "Период"
                      }
                      span="Период"
                      name="period"
                      disabled={false}
                      style={{ width: "150px", height: "42px" }}
                      isShowPlaceholder={
                        inputValues?.period && inputValues?.period
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            <ArrowIcon
              style={{ visibility: isVisionRightButton ? "visible" : "hidden" }}
              className={clsx(styles.map__arrow, styles.map__arrow_right)}
              onClick={handleClickOnRight}
            />
          </div>
          <div
            style={{ borderRadius: "10px", overflow: "hidden", height: "100%" }}
          >
            <YMaps query={{ apikey: `${YMAP_API_KEY}`, lang: "ru_RU" }}>
              {" "}
              <Map
                state={{
                  center: mapCenter,
                  zoom: zoom,
                  type: "yandex#map",
                }}
                options={{
                  yandexMapDisablePoiInteractivity: true,
                  suppressMapOpenBlock: true,
                }}
                style={{ width: "100%", height: "100%" }}
                modules={["package.full"]}
                instanceRef={(ref) => {
                  if (ref) {
                    ref.controls.remove("searchControl");
                  }
                }}
                className={darkTheme.isDarkTheme ? styles.darkMap : ""}
              >
                {data && (
                  <Clusterer
                    key={data.length}
                    options={{
                      clusterIconLayout: "default#pieChart",
                      clusterIconPieChartRadius: 25,
                      clusterIconPieChartCoreRadius: 10,
                      clusterIconPieChartStrokeWidth: 1,
                      clusterDisableClickZoom: true,
                      clusterHideIconOnBalloonOpen: false,
                      geoObjectHideIconOnBalloonOpen: false,
                      hideIconOnBalloonOpen: false,
                      groupByCoordinates: false,
                      openBalloonOnClick: true,
                      clusterBalloonMaxHeight: 850,
                      clusterBalloonMinHeight: 240,
                      clusterBalloonMinWidth: 250,
                      clusterBalloonMaxWidth: 850,
                      maxZoom: 16,
                    }}
                    onClick={(e: any) => {
                      if (e.get("target").getGeoObjects) {
                        const cluster = e.get("target");
                        const clusterGeoObjects =
                          cluster.properties.get("geoObjects");

                        if (clusterGeoObjects.length > 8) {
                          const map = e.get("map");
                          const newZoom = Math.min(map.getZoom() + 3.3, 18);
                          map.setCenter(
                            cluster.geometry.getCoordinates(),
                            newZoom
                          );
                        } else {
                          cluster.state.set("activeObject", cluster);
                          const allPointIds: number[] = [];
                          clusterGeoObjects.forEach((geoObject: any) => {
                            const pointId =
                              geoObject.properties.get("data").pointId;
                            if (!allPointIds.includes(pointId)) {
                              allPointIds.push(pointId);
                            }
                          });

                          allPointIds.forEach((el: number) => {
                            dispatch(
                              actionGetUserReports(
                                [el],
                                inputValues.contract[0].id
                              )
                            ).then((res) => {
                              setIsReport(
                                (
                                  prevState:
                                    | { key: number; value: IReportsOptions }[]
                                    | null
                                ) => [
                                  ...(prevState || []),
                                  { key: el, value: res },
                                ]
                              );
                            });
                          });
                        }
                      }
                    }}
                    modules={["clusterer.addon.balloon"]}
                  >
                    {data &&
                      data.length > 0 &&
                      [...data]
                        .sort((a, b) => a.pos.id - b.pos.id)
                        .map((point: any, index: number) => {
                          const coordinates = [
                            point?.longitude,
                            point?.latitude,
                          ];
                          const uniqueCoordinates =
                            getUniqueCoordinates(coordinates);
                          const colorPoint: any = typeOptionsColor.find(
                            (option: { value: string; color: string }) =>
                              option.value === Object.keys(point.status)[0]
                          );
                          const days = point?.days || [];
                          const uniqueDays = Array.from(new Set(days));
                          const isShowInfo =
                            point.status &&
                            (Object.keys(point.status)[0] === "Active" ||
                              Object.keys(point.status)[0] === "Paused" ||
                              Object.keys(point.status)[0] === "Vacancy");

                          const merchStatus = point?.merch?.map(
                            (el: any) => Object.values(el.status)[0]
                          );
                          const merchName = point?.merch?.map((el: any) => {
                            return `${el?.givenName ? ` ` : ""}${
                              el?.givenName || el?.middleName || el?.middleName
                                ? ` (${
                                    el?.familyName
                                      ? el?.givenName
                                        ? `${el?.familyName} `
                                        : ``
                                      : ""
                                  }${
                                    el?.givenName
                                      ? el?.middleName
                                        ? `${el?.givenName} `
                                        : ``
                                      : ""
                                  }${el?.middleName ? el?.middleName : ""})`
                                : ""
                            }`;
                          });
                          const svName = point?.sv?.map(
                            (el: any) =>
                              `${el?.familyName ? `${el?.familyName}` : ""}${
                                el.givenName ? ` ${el?.givenName}` : ""
                              }${el?.middleName ? ` ${el.middleName}` : ""}${
                                el?.phone ? `: ${el?.phone}` : ""
                              }`
                          );
                          const vsvName = point?.vsv?.map(
                            (el: any) =>
                              `${el?.familyName ? `${el?.familyName}` : ""}${
                                el.givenName ? ` ${el?.givenName}` : ""
                              }${el?.middleName ? ` ${el.middleName}` : ""}${
                                el?.phone ? `: ${el?.phone}` : ""
                              }`
                          );
                          const status = Object.keys(point?.status).toString();

                          const linkId =
                            isReport && isReport.length > 0
                              ? isReport.find(
                                  (el: {
                                    key: number;
                                    value: IReportsOptions;
                                  }) => {
                                    return el.key === point.pos.id;
                                  }
                                )
                              : null;

                          const textLink =
                            linkId && linkId.value
                              ? `№${
                                  linkId.value.reportId
                                } от ${formatDateForMapReport(
                                  linkId.value.expected
                                )}`
                              : "";
                          const login = point?.merch?.map(
                            (el: any) => el.login
                          );

                          return (
                            <Placemark
                              key={index}
                              onLoad={(ymapsInstance) =>
                                setYmaps(ymapsInstance)
                              }
                              onClick={() => {
                                setIsReport([]);
                                if (isShowInfo) {
                                  dispatch(
                                    actionGetUserReports(
                                      [point.pos.id],
                                      inputValues.contract[0].id
                                    )
                                  ).then((res) => {
                                    setIsReport([
                                      { key: point.pos.id, value: res },
                                    ]);
                                  });
                                }
                              }}
                              options={{
                                preset: "islands#violetIcon",
                                iconColor: colorPoint.color || "green",
                                balloonMaxWidth: 800,
                                iconLayout: layout({
                                  rating: point?.rating ? point?.rating : 0,
                                  status: Object.keys(point?.status)[0],
                                  days: days && uniqueDays,
                                  quality: point.quality || null,
                                }),
                                iconImageSize: [35, 70],
                                iconOffset: [-18, -35],
                                hideIconOnBalloonOpen: false,
                              }}
                              geometry={uniqueCoordinates}
                              properties={{
                                data: {
                                  ind: index,
                                  pointId: point.pos.id,
                                },
                                balloonContent: `<div>
                              <p>Сеть: ${point?.chain}</p>
                                <p>Адрес: ${
                                  point.city ? `г. ${point.city}, ` : ""
                                }${point?.pos?.name ? point?.pos?.name : ""}</p>
                                <p>Статус: ${
                                  typeTextOptions[
                                    status as keyof typeof typeTextOptions
                                  ]
                                }</p>
                                ${
                                  isShowInfo
                                    ? `<p>День визита: ${uniqueDays.join(
                                        ", "
                                      )}</p>`
                                    : ""
                                }
                                ${
                                  isShowInfo
                                    ? `<p>Статус мерчендайзера: ${merchStatus.join(
                                        "; "
                                      )}</p>`
                                    : ""
                                }
                                ${
                                  isShowInfo
                                    ? `<p>ФИО мерчендайзера: ${login}  ${merchName.join(
                                        "; "
                                      )}}
                                </p>`
                                    : ""
                                }
                                ${
                                  isShowInfo
                                    ? `<p>ФИО супервайзера(телефон): ${svName.join(
                                        "; "
                                      )}
                                </p>`
                                    : ""
                                }
                                ${
                                  isShowInfo
                                    ? `<p>ФИО ВСВ(телефон): ${vsvName.join(
                                        "; "
                                      )}
                                </p>`
                                    : ""
                                }
                                ${
                                  isShowInfo
                                    ? `<p>Последний отчёт: <a target="_blank" href="/reports/${
                                        linkId && linkId.value
                                          ? linkId.value.reportId
                                          : ""
                                      }" class="balloon-link">${textLink}</a>
                              </p>
                              </div>`
                                    : ""
                                }`,
                                clusterCaption: `${point?.chain}`,
                              }}
                              modules={[
                                "geoObject.addon.balloon",
                                "templateLayoutFactory",
                              ]}
                            />
                          );
                        })}
                  </Clusterer>
                )}{" "}
                <RulerControl /> <ZoomControl />{" "}
              </Map>{" "}
            </YMaps>
          </div>
        </div>
      </div>
    </div>
  );
};
export default CustomerMap;
